import React, { useState, useEffect, useRef } from "react";
import Loader from "../../Shared/Loader";

import axios from "axios";
import "../../../css/QuestionnaireVersions.css";
import { ViewQuestionsModal, FullQuestion } from "./ViewQuestionsModal";
import { showToast } from "../../Shared/Toaster";

// --- Configuration ---
const API_BASE_URL = "https://naucnelku4.execute-api.ap-south-2.amazonaws.com/dev/questionnair/api";
// const API_BASE_URL = "http://localhost:7000/api"; // For local development

// --- TypeScript Interfaces ---
interface AggregatedVersion {
  id: number;
  status: "Active" | "Inactive";
  total: number;
  created: string;
  updated: string;
}

interface QuestionSet {
  id: string;
  setTitle: string;
  question_count: number;
  created_on: string;
  status: "Active" | "Inactive";
}

interface QuestionsApiResponse {
  version: number;
  status: "active" | "inactive";
  createdAt: string;
  questions: FullQuestion[];
}

interface RawQuestionnaireDoc {
  _id: string; 
  categorySlug: string; 
  version: number; 
  status: string; 
  questions: any[]; 
  createdAt: string; 
  updatedAt: string;
}

interface QuestionnaireVersionsProps {
  categorySlug: string;
  categoryTitle: string;
}

const QuestionnaireVersions: React.FC<QuestionnaireVersionsProps> = ({ categorySlug, categoryTitle }) => {
  // --- State ---
  const [versions, setVersions] = useState<AggregatedVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activatingId, setActivatingId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedVersion, setSelectedVersion] = useState<AggregatedVersion | null>(null);
  const [questionSets, setQuestionSets] = useState<QuestionSet[]>([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState<{ set: QuestionSet, questions: FullQuestion[] } | null>(null);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- Helper Functions ---
  const processDataForCategory = (data: RawQuestionnaireDoc[]): AggregatedVersion[] => {
    const aggregationMap = new Map<number, {
      total: number;
      created: Date;
      updated: Date;
      hasActiveStatus: boolean;
    }>();

    for (const doc of data) {
      const existing = aggregationMap.get(doc.version);
      const docCreatedDate = new Date(doc.createdAt);
      const docUpdatedDate = new Date(doc.updatedAt);
      if (!existing) {
        aggregationMap.set(doc.version, {
          total: doc.questions.length,
          created: docCreatedDate,
          updated: docUpdatedDate,
          hasActiveStatus: doc.status === "active",
        });
      } else {
        existing.total += doc.questions.length;
        if (docCreatedDate < existing.created) existing.created = docCreatedDate;
        if (docUpdatedDate > existing.updated) existing.updated = docUpdatedDate;
        if (doc.status === "active") existing.hasActiveStatus = true;
      }
    }
    const formattedData: AggregatedVersion[] = Array.from(aggregationMap.entries()).map(([version, data]) => ({
      id: version,
      status: data.hasActiveStatus ? "Active" : "Inactive",
      total: data.total,
      created: data.created.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      updated: data.updated.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    }));
    return formattedData.sort((a, b) => a.id - b.id);
  };

  const processQuestionSets = (apiResponse: QuestionsApiResponse, createdDate: string): QuestionSet[] => {
    const setsMap = new Map<string, { count: number }>();
    for (const question of apiResponse.questions) {
      const title = question.subsectionTitle;
      if (setsMap.has(title)) {
        setsMap.get(title)!.count++;
      } else {
        setsMap.set(title, { count: 1 });
      }
    }
    const processedSets: QuestionSet[] = Array.from(setsMap.entries()).map(([title, data]) => ({
      id: title,
      setTitle: title,
      question_count: data.count,
      created_on: createdDate,
      status: apiResponse.status === 'active' ? 'Active' : 'Inactive',
    }));
    return processedSets;
  };

  // --- Data Fetching ---
  const fetchVersions = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const endpoint = `${API_BASE_URL}/categories/${categorySlug}/versions`;
      const response = await axios.get<RawQuestionnaireDoc[]>(endpoint);
      setVersions(processDataForCategory(response.data));
    } catch (err) {
      setError(`Failed to fetch versions for ${categoryTitle}.`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVersions();
  }, [categorySlug]);

  useEffect(() => {
    if (!selectedVersion) return;
    const fetchQuestionSets = async () => {
      try {
        setIsTableLoading(true);
        setError(null);
        const endpoint = `${API_BASE_URL}/categories/${categorySlug}/questions`;
        const response = await axios.get<QuestionsApiResponse>(endpoint, { params: { version: selectedVersion.id } });
        setQuestionSets(processQuestionSets(response.data, selectedVersion.created));
      } catch (err) {
        setError(`Failed to fetch details for Version ${selectedVersion.id}.`);
        console.error(err);
      } finally {
        setIsTableLoading(false);
      }
    };
    fetchQuestionSets();
  }, [selectedVersion, categorySlug]);

  // --- Handlers ---
  const handleImportClick = () => fileInputRef.current?.click();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    setUploadMessage(`Uploading ${file.name}...`);
    setError(null);
    const formData = new FormData();
    formData.append("file", file);
    // You might want to send the slug with the upload too
    // formData.append("categorySlug", categorySlug); 
    try {
      const uploadEndpoint = `${API_BASE_URL}/import-csv`;
      const response = await axios.post(uploadEndpoint, formData);
      const msg = response.data.message || "File uploaded successfully!";
      setUploadMessage(msg);
      showToast(msg, "success");
      await fetchVersions();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "An error occurred during upload.";
      setError(errorMessage);
      showToast(errorMessage, "error");
      setUploadMessage("");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };
  
  const handleActivate = async (versionId: number) => {
    const confirmActivation = window.confirm(`Are you sure you want to activate Version ${versionId}.0 for ${categoryTitle}?`);
    if (confirmActivation) {
      setActivatingId(versionId);
      setError(null);
      try {
        const endpoint = `${API_BASE_URL}/categories/${categorySlug}/activate/${versionId}`;
        await axios.post(endpoint);
        showToast("Version activated successfully ✅", "success");
        await fetchVersions();
      } catch (err) {
        setError("Failed to activate version. Please try again.");
        showToast("Failed to activate version. Please try again. ❌", "error");
        console.error(err);
      } finally {
        setActivatingId(null);
      }
    }
  };

  const handleCardClick = (version: AggregatedVersion) => {
    if (version.status === 'Inactive') handleActivate(version.id);
  };

  const handleViewDetailsClick = (e: React.MouseEvent, version: AggregatedVersion) => {
    e.stopPropagation();
    e.preventDefault();
    setViewMode('list');
    setSelectedVersion(version);
  };

  const handleBackToGrid = () => {
    setViewMode('grid');
    setSelectedVersion(null);
    setQuestionSets([]);
  };

  const handleRowClick = async (set: QuestionSet) => {
    if (!selectedVersion) return;
    setIsModalLoading(true);
    setIsModalOpen(true);
    try {
        const endpoint = `${API_BASE_URL}/categories/${categorySlug}/questions`;
        const response = await axios.get<QuestionsApiResponse>(endpoint, { 
            params: { version: selectedVersion.id, subsection: set.setTitle } 
        });
        setModalData({ set: set, questions: response.data.questions });
    } catch (err) {
        setError(`Failed to load questions for ${set.setTitle}.`);
        setIsModalOpen(false);
    } finally {
        setIsModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalData(null);
  };

  // --- Render Logic ---
  const renderHeader = () => (
    <div className="category-header">
      <h2 className="category-title">{categoryTitle}</h2>
      {viewMode === 'grid' && (
        <button className="import-button" onClick={handleImportClick} disabled={isUploading || !!activatingId}>
          {isUploading ? "Uploading..." : "Import File"}
        </button>
      )}
    </div>
  );

  if (isLoading) return <div className="questionnaire-management-page"><Loader text={`Loading ${categoryTitle}...`} /></div>;

  return (
    <div className="questionnaire-management-page">
      {viewMode === 'grid' && (
        <div className="category-section">
          {renderHeader()}
          <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: "none" }} />
          <div className="feedback-messages">
            {uploadMessage && <p className="success-message">{uploadMessage}</p>}
            {error && <p className="error-message">{error}</p>}
          </div>
          <div className="versions-container">
            {versions.map((v) => (
              <div 
                className={`version-card ${v.status === 'Inactive' ? 'clickable' : ''}`} 
                key={v.id}
                onClick={() => handleCardClick(v)}
              >
                <div>
                  <h3>Version {v.id}.0</h3>
                  <span className={`status ${v.status.toLowerCase()}`}>{v.status}</span>
                  <p>Total Questionnaires: {v.total}</p>
                  <p>Created: {v.created}</p>
                  <p>Last updated: {v.updated}</p>
                </div>
                <div className="card-actions">
                  <a href="#" className="view-link" onClick={(e) => handleViewDetailsClick(e, v)}>
                    View Questionnaires
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {viewMode === 'list' && (
        <div className="list-view-container">
          <div className="list-sidebar">
            <button onClick={handleBackToGrid} className="back-button">← Back</button>
            <h3 className="sidebar-title">All Versions</h3>
            {versions.map(v => (
              <div 
                key={v.id} 
                className={`list-item ${selectedVersion?.id === v.id ? 'selected' : ''}`}
                onClick={() => setSelectedVersion(v)}
              >
                <strong>Version {v.id}.0</strong>
                <span className={`status-sm ${v.status.toLowerCase()}`}>{v.status}</span>
              </div>
            ))}
          </div>
          <div className="list-content">
            {renderHeader()}
            <h3 className="details-title">Details for Version {selectedVersion?.id}.0</h3>
            {isTableLoading ? (
              <Loader text="Loading details..." />
            ) : (
              <table className="details-table">
                <thead>
                  <tr>
                    <th>Set Title</th>
                    <th>Total Questions in Set</th>
                    <th>Created On</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {questionSets.length > 0 ? (
                    questionSets.map(set => (
                      <tr key={set.id} onClick={() => handleRowClick(set)} className="clickable-row">
                        <td>{set.setTitle}</td>
                        <td>{set.question_count}</td>
                        <td>{new Date(set.created_on).toLocaleDateString()}</td>
                        <td><span className={`status ${set.status.toLowerCase()}`}>{set.status}</span></td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan={4}>No question sets found for this version.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

  {isModalOpen && selectedVersion && (
    isModalLoading ? (
        <div className="modal-overlay"><Loader text="Loading Questions..." /></div>
    ) : modalData && (
            <ViewQuestionsModal
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              version={selectedVersion.id}
              setTitle={modalData.set.setTitle}
              totalQuestions={modalData.set.question_count}
              createdOn={modalData.set.created_on}
              questions={modalData.questions}
            />
        )
      )}
    </div>
  );
};

export default QuestionnaireVersions;